import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  IconButton,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
// @ts-ignore
import { ResizableBox, ResizableCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";

// データ型
export type BaseListItem = {
  id: string | number;
  baseName?: string;
  address?: string;
  phoneNum?: string;
  mailAddress?: string;
  postNum?: string;
};

type Props = {
  bases: BaseListItem[];
  checkedIds: Set<string>;
  onCheckedChange: (id: string) => void;
  onCheckedChangeAll: (checkedIds: Set<string>) => void;
  onRowClick: (base: BaseListItem) => void;
  onShowUserList: (base: BaseListItem) => void;
  footerHeight: number;
};

const BaseListTable: React.FC<Props> = ({
  bases,
  checkedIds,
  onCheckedChange,
  onCheckedChangeAll,
  onRowClick,
  onShowUserList,
  footerHeight,
}) => {
  // フィルター条件
  const [filters, setFilters] = useState<Partial<BaseListItem>>({});
  const [columnWidths, setColumnWidths] = useState<number[]>([
    20, // チェックボックス
    80,
    250, // 拠点名
    300, // 住所
    180, // 代表電話番号
    220, // 代表メールアドレス
  ]);

  // フィルター条件の更新
  const handleFilterChange = (key: keyof BaseListItem, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // フィルタリング
  const filteredBases = bases.filter((base) => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      const targetValue = base[key as keyof BaseListItem] ?? "";
      return String(targetValue).includes(String(value));
    });
  });

  // 列幅更新
  const handleResize = (index: number, newWidth: number) => {
    const updated = [...columnWidths];
    updated[index] = newWidth;
    setColumnWidths(updated);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "auto",
        height: `calc(100vh - 130px - ${
          checkedIds.size > 0 ? footerHeight * 1.3 : 0
        }px)`,
        overflowY: "auto",
        overflowX: "auto",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{ minWidth: 1400, overflowX: "auto" }}
      >
        <TableHead>
          {/* ヘッダー行 */}
          <TableRow>
            <TableCell align="center" sx={{ width: 20, padding: 0 }}>
              <Checkbox
                checked={
                  checkedIds.size === filteredBases.length &&
                  filteredBases.length > 0
                }
                indeterminate={
                  checkedIds.size > 0 && checkedIds.size < filteredBases.length
                }
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    const newSet = new Set(
                      filteredBases.map((base) => String(base.id))
                    );
                    onCheckedChangeAll(newSet);
                  } else {
                    onCheckedChangeAll(new Set());
                  }
                }}
              />
            </TableCell>
            {[
              "ユーザーリスト",
              "拠点名",
              "住所",
              "代表電話番号",
              "代表メールアドレス",
            ].map((label, idx) => (
              <TableCell
                key={label}
                align="center"
                style={{ width: columnWidths[idx + 1], whiteSpace: "nowrap" }}
              >
                <ResizableBox
                  width={columnWidths[idx + 1]}
                  height={30}
                  axis="x"
                  resizeHandles={["e"]}
                  onResizeStop={(_: unknown, data: ResizableCallbackData) => {
                    handleResize(idx + 1, data.size.width);
                  }}
                >
                  <div
                    style={{
                      width: columnWidths[idx + 1],
                      textAlign: "center",
                    }}
                  >
                    {label}
                  </div>
                </ResizableBox>
              </TableCell>
            ))}
          </TableRow>

          {/* フィルター行 */}
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("baseName", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("address", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("phoneNum", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) =>
                  handleFilterChange("mailAddress", e.target.value)
                }
              />
            </TableCell>
          </TableRow>
        </TableHead>

        {/* データ行 */}
        <TableBody>
          {filteredBases.map((base) => (
            <TableRow
              key={base.id}
              onClick={() => {
                onRowClick(base);
              }}
            >
              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checkedIds.has(String(base.id))}
                  onChange={(e) => {
                    e.stopPropagation();
                    onCheckedChange(String(base.id));
                  }}
                />
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowUserList(base);
                  }}
                >
                  <PeopleAltIcon fontSize="small" />
                </IconButton>
              </TableCell>
              <TableCell>{base.baseName}</TableCell>
              <TableCell>{base.address}</TableCell>
              <TableCell>{base.phoneNum}</TableCell>
              <TableCell>{base.mailAddress}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BaseListTable;
